import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import { AppCTX } from '../data/AppContext';

import TeamSignUp from './TeamSignUp';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const Login = () => {
  const { login, setUser } = React.useContext(AppCTX);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSignUp, setShowSignUp] = useState(false);

  // If user wants to sign up with invite code, show TeamSignUp component
  if (showSignUp) {
    return <TeamSignUp onBack={() => setShowSignUp(false)} />;
  }

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/email/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.error || 'Login failed');
      }

      const { session, data } = result;
      const success = await login(
        session.access_token,
        session.refresh_token,
        data,
        session.expires_in
      );
      if (!success) {
        throw new Error('Failed to store session');
      }
      setUser({ uid: data.id, data });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100vw',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0b0b0b',
      }}
    >
      {/* Background video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          minWidth: '100%',
          minHeight: '100%',
          width: 'auto',
          height: 'auto',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          objectFit: 'cover',
        }}
      >
        <source src={process.env.PUBLIC_URL + '/bg-video.mp4'} type="video/mp4" />
      </video>

      {/* Dark overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.9) 100%)',
          zIndex: 2,
        }}
      />

      {/* Card */}
      <Paper
        elevation={8}
        sx={{
          position: 'relative',
          zIndex: 3,
          width: '90%',
          maxWidth: 420,
          p: 3,
          borderRadius: 3,
          background: 'rgba(18,18,18,0.85)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <img
            src={process.env.PUBLIC_URL + '/logo.jpeg'}
            alt="Delta Ops"
            style={{ width: 120, height: 'auto', borderRadius: 12 }}
          />
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
            Delta Ops
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Sign in with email
          </Typography>

          <TextField
            fullWidth
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputLabelProps={{ style: { color: '#bbb' } }}
            InputProps={{ style: { color: '#fff' } }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputLabelProps={{ style: { color: '#bbb' } }}
            InputProps={{ style: { color: '#fff' } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLogin();
            }}
          />

          {error && (
            <Typography variant="body2" color="error" sx={{ width: '100%' }}>
              {error}
            </Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleLogin}
            disabled={loading}
            sx={{
              mt: 1,
              bgcolor: '#fff',
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: '#e0e0e0' },
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>

          {/* Toggle to Sign Up */}
          <Button
            variant="text"
            fullWidth
            onClick={() => setShowSignUp(true)}
            sx={{ color: 'rgba(255,255,255,0.7)' }}
          >
            Have an invite code? Sign Up
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Login;
